'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { CheckCircle2, Eye, EyeOff, Loader2, Lock, XCircle, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from '@/data/config/firebase-config';

type Status = 'pending' | 'working' | 'success' | 'error' | 'needsPassword';

const errorMessages: Record<string, string> = {
  'auth/expired-action-code': 'Ο σύνδεσμος έχει λήξει. Ζητήστε νέο.',
  'auth/invalid-action-code': 'Ο σύνδεσμος δεν είναι έγκυρος ή έχει ήδη χρησιμοποιηθεί.',
  'auth/user-disabled': 'Ο λογαριασμός σας έχει απενεργοποιηθεί.',
  'auth/user-not-found': 'Δεν βρέθηκε ο λογαριασμός.',
  'auth/weak-password': 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.',
};

const getErrorMessage = (error: unknown) => {
  const code = (error as { code?: string })?.code ?? '';
  return errorMessages[code] ?? 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.';
};

const AuthAction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');

  const [status, setStatus] = useState<Status>('pending');
  const [title, setTitle] = useState('Επαλήθευση συνδέσμου...');
  const [message, setMessage] = useState('Παρακαλώ περιμένετε λίγο.');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldError, setFieldError] = useState('');
  // Firebase invalidates an oobCode on first use, so React StrictMode's double
  // effect would otherwise report a valid link as already consumed.
  const handled = useRef(false);

  // continueUrl is supplied by Firebase; only follow it if it points back at this app.
  const safeContinueUrl = useCallback(() => {
    if (!continueUrl) return null;
    try {
      const target = new URL(continueUrl, window.location.origin);
      return target.origin === window.location.origin ? target.pathname + target.search : null;
    } catch {
      return null;
    }
  }, [continueUrl]);

  const goToApp = useCallback(() => {
    navigate(safeContinueUrl() ?? '/login');
  }, [navigate, safeContinueUrl]);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const run = async () => {
      if (!mode || !oobCode) {
        setStatus('error');
        setTitle('Μη έγκυρος σύνδεσμος');
        setMessage('Ο σύνδεσμος δεν περιέχει τα απαραίτητα στοιχεία.');
        return;
      }

      setStatus('working');

      try {
        switch (mode) {
          case 'verifyEmail': {
            await applyActionCode(auth, oobCode);
            // Refresh the local user so emailVerified is up to date in this tab.
            await auth.currentUser?.reload();
            setStatus('success');
            setTitle('Το email σας επαληθεύτηκε!');
            setMessage('Ο λογαριασμός σας είναι έτοιμος. Μπορείτε να συνεχίσετε.');
            break;
          }
          case 'resetPassword': {
            const email = await verifyPasswordResetCode(auth, oobCode);
            setStatus('needsPassword');
            setTitle('Νέος κωδικός πρόσβασης');
            setMessage(`Ορίστε νέο κωδικό για τον λογαριασμό ${email}.`);
            break;
          }
          case 'recoverEmail': {
            const info = await checkActionCode(auth, oobCode);
            await applyActionCode(auth, oobCode);
            setStatus('success');
            setTitle('Το email σας επαναφέρθηκε');
            setMessage(
              `Η διεύθυνση ${info.data.email ?? 'του λογαριασμού σας'} ενεργοποιήθηκε ξανά. Συνιστούμε να αλλάξετε τον κωδικό σας.`,
            );
            break;
          }
          default: {
            setStatus('error');
            setTitle('Μη υποστηριζόμενη ενέργεια');
            setMessage('Δεν μπορούμε να επεξεργαστούμε αυτόν τον σύνδεσμο.');
          }
        }
      } catch (error) {
        setStatus('error');
        setTitle('Ο σύνδεσμος δεν λειτουργεί');
        setMessage(getErrorMessage(error));
      }
    };

    run();
  }, [mode, oobCode]);

  const handlePasswordReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!oobCode) return;

    if (password.length < 6) {
      setFieldError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.');
      return;
    }
    if (password !== confirmPassword) {
      setFieldError('Οι κωδικοί δεν ταιριάζουν.');
      return;
    }

    setFieldError('');
    setStatus('working');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
      setTitle('Ο κωδικός σας άλλαξε!');
      setMessage('Μπορείτε τώρα να συνδεθείτε με τον νέο σας κωδικό.');
    } catch (error) {
      setStatus('error');
      setTitle('Η αλλαγή απέτυχε');
      setMessage(getErrorMessage(error));
    }
  };

  const isBusy = status === 'working' || status === 'pending';

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-slate-50 p-6">
      {/* Ambient glows to match the login screen */}
      <div className="pointer-events-none absolute -top-32 -right-24 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white p-10 shadow-2xl shadow-slate-900/5"
      >
        <div className="flex flex-col items-center space-y-5 text-center">
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg',
              status === 'success' && 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/25',
              status === 'error' && 'bg-gradient-to-br from-red-500 to-rose-600 shadow-red-500/25',
              (isBusy || status === 'needsPassword') &&
                'bg-gradient-to-br from-blue-500 to-purple-600 shadow-purple-500/25',
            )}
          >
            {status === 'success' && <CheckCircle2 className="h-7 w-7 text-white" />}
            {status === 'error' && <XCircle className="h-7 w-7 text-white" />}
            {isBusy && <Loader2 className="h-7 w-7 animate-spin text-white" />}
            {status === 'needsPassword' && <Zap className="h-7 w-7 text-white" />}
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
            <p className="text-sm leading-relaxed text-slate-500">{message}</p>
          </div>
        </div>

        {status === 'needsPassword' && (
          <form onSubmit={handlePasswordReset} className="mt-8 space-y-4" noValidate>
            <div className="relative">
              <Lock className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Νέος κωδικός"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50/80 pr-11 pl-11 text-sm text-slate-900 shadow-sm transition-all outline-none placeholder:text-slate-400 hover:bg-white focus:border-purple-400 focus:bg-white focus:ring-4 focus:ring-purple-500/10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute top-1/2 right-3.5 -translate-y-1/2 text-slate-400 transition-colors hover:text-purple-600"
                aria-label={showPassword ? 'Απόκρυψη κωδικού' : 'Εμφάνιση κωδικού'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            <div className="relative">
              <Lock className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Επιβεβαίωση κωδικού"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50/80 pr-4 pl-11 text-sm text-slate-900 shadow-sm transition-all outline-none placeholder:text-slate-400 hover:bg-white focus:border-purple-400 focus:bg-white focus:ring-4 focus:ring-purple-500/10"
              />
            </div>

            {fieldError && <p className="text-xs font-medium text-red-500">{fieldError}</p>}

            <button
              type="submit"
              className="group relative flex h-12 w-full items-center justify-center overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-base font-semibold text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/30"
            >
              <div className="absolute inset-0 translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-1000 group-hover:-translate-x-full" />
              <span className="relative">Αποθήκευση κωδικού</span>
            </button>
          </form>
        )}

        {(status === 'success' || status === 'error') && (
          <button
            onClick={goToApp}
            className="group relative mt-8 flex h-12 w-full items-center justify-center overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-base font-semibold text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/30"
          >
            <div className="absolute inset-0 translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-1000 group-hover:-translate-x-full" />
            <span className="relative">{status === 'success' ? 'Συνέχεια' : 'Επιστροφή στη σύνδεση'}</span>
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default AuthAction;
